<template>
  <div>
    <div v-if="!isLoading && budgets.length === 0" class="no-budgets">
      <img style="height: 180px; filter: grayscale(1); opacity: 0.6" :src="require('@/assets/no_budget.png')" />
      <h3>Sem orçamentos cadastrados</h3>
      <small
        >Altere algum dos filtros para buscar novamente ou <br />
        tente novamente mais tarde.</small
      >
    </div>

    <div class="list-container">
      <LazyList :objectItems="groupedBudgets" @load-more="loadMoreData" :has-more-data="hasMoreData">
        <template v-slot:default="{ items }">
          <div v-for="(group, groupIndex) in items" :key="groupIndex">
            <div class="date">{{ group.date }}</div>
            <div v-for="(budget, budgetIndex) in group.items" :key="budgetIndex" class="item" @click="handleSelect(budget)">
              <div class="first-info" style="display: flex; justify-content: space-between; gap: 8px">
                <div class="client-info">
                  <div class="client-vehicle">{{ budget.vehicle ? budget.vehicle.description : 'Veículo não informado' }}</div>
                  <div class="client-name">{{ budget.client ? budget.client.name : 'Cliente não informado' }}</div>
                </div>
                <div v-if="budget.vehicle" class="vehicle-plate">
                  <img :src="budget.vehicle.vehicle_plate_photo_url" />
                </div>
              </div>
              <div class="budget-info">
                <div class="status">{{ getBudgetStatus(budget.status).text }}</div>
                <div class="date-price">
                  {{ budget.date ? formatDate(budget.date) : 'Sem data' }} · {{ calculateTotal(budget) }} ·
                  {{ calculateEstimate(budget) }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </LazyList>

      <div v-if="isLoading" class="loader">
        <v-row v-for="loaderIndex in 3" :key="loaderIndex">
          <v-col cols="12">
            <v-skeleton-loader type="list-item-three-line" style="border-bottom: 3px solid #e0e0e0"></v-skeleton-loader>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-dialog v-model="isComponentLoading" persistent width="300" class="loader-service-order">
      <v-card class="pt-5 pb-2" color="primary" dark>
        <v-card-text>
          Carregando orçamento...
          <v-progress-linear indeterminate color="white" class="mb-0 mt-2"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import LazyList from '@/components/Common/LazyList';
import BudgetService from '@/services/api/budget';

export default {
  emits: ['select', 'load-more'],
  props: {
    budgets: {
      type: Array,
      default: () => []
    }
  },
  components: {
    LazyList
  },
  data() {
    return {
      isComponentLoading: false,
      status: [
        { value: 'pendente', text: 'Pendente' },
        { value: 'aguardando_aprovacao', text: 'Aguardando aprovação' },
        { value: 'aguardando_pagamento', text: 'Aguardando pagamento' },
        { value: 'em_andamento', text: 'Em andamento' },
        { value: 'aprovado', text: 'Aprovado' },
        { value: 'cancelado', text: 'Cancelado' },
        { value: 'recusado', text: 'Recusado' }
      ]
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters['Budget/isLoading'];
    },
    hasMoreData() {
      return this.$store.getters['Budget/hasMoreData'];
    },
    groupedBudgets() {
      return this.groupBudgetsByDay(this.budgets);
    }
  },
  methods: {
    getBudgetStatus(statusRef) {
      return this.status.find((status) => status.value === statusRef);
    },
    handleSelect(budgetServiceOrder) {
      this.isComponentLoading = true;

      BudgetService.getById(budgetServiceOrder.id)
        .then((response) => {
          const budget = response.data;

          this.$store.commit('Budget/SET_SELECTED_BUDGET', budget);
          this.$emit('select', budget);
        })
        .finally(() => {
          this.isComponentLoading = false;
        });
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    formatCurrency(amount) {
      return amount ? parseFloat(amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';
    },
    calculateEstimate(budget) {
      if (budget?.total_estimate > 0) {
        const estimate = budget.total_estimate;

        if (estimate < 60) {
          return `${estimate} minutos`;
        }

        const hours = Math.floor(estimate / 60);
        const minutes = estimate % 60;

        return `${hours}h${minutes.toString().padStart(2, '0')}m`;
      }

      return 'Sem estimativa';
    },
    calculateDiscount(budget) {
      const discountType = budget.discount_type;
      const { subtotal, discount } = budget;

      if (discount) {
        if (discountType === 'percentual') {
          let discountedAmount;

          if (typeof discount === 'string') {
            const cleanedDiscount = discount.replace(/[^\d,.-]/g, '');
            const parsedDiscount = parseFloat(cleanedDiscount.replace(',', '.'));

            discountedAmount = subtotal * (parsedDiscount / 100);
          } else {
            discountedAmount = subtotal * (discount / 100);
          }

          return this.formatCurrency(discountedAmount);
        }

        return this.formatCurrency(discount);
      }

      return this.formatCurrency(0);
    },
    calculateTotal(budget) {
      const subTotal = budget.subtotal;
      const discount = this.calculateDiscount(budget);

      return this.formatCurrency(subTotal - this.parseCurrency(discount));
    },
    parseCurrency(value) {
      return value ? parseFloat(value.replace('R$', '').replace('.', '').replace(',', '.').trim()) : 0;
    },
    loadMoreData() {
      if (this.hasMoreData && !this.isLoading) {
        this.$emit('load-more');
      }
    },
    groupBudgetsByDay(budgets) {
      const groupedBudgets = {};

      budgets.forEach((budget) => {
        const date = new Date(budget.date).toLocaleDateString();

        if (!groupedBudgets[date]) {
          groupedBudgets[date] = [];
        }

        groupedBudgets[date].push(budget);
      });

      return groupedBudgets;
    }
  }
};
</script>

<style lang="scss" scoped>
.no-budgets {
  text-align: center;
  padding: 20px;
  color: #a6a4a4;
}

.list-container {
  width: -webkit-fill-available;
  margin-bottom: 80px;

  .date {
    font-size: 12px;
    color: #667781;
    padding: 10px 20px;
    border-bottom: 1px solid #e9edef;
    background-color: #f5f5f5;
  }

  .item {
    border-bottom: 3px solid #e0e0e0;
    padding: 10px 20px;
    overflow: hidden;

    .vehicle-plate {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px 8px;
      background-color: #eee;
      overflow: hidden;
      border-radius: 4px;
      width: 90px;

      img {
        float: left;
        width: 100%;
      }

      span {
        float: left;
        font-size: 12px;
        overflow: hidden;
        width: 100%;
        line-height: 19px;
        text-align: center;
        background-color: #eeeeee;
        border-bottom-right-radius: 5px;
        border-bottom-left-radius: 5px;
      }
    }

    .budget-info {
      border-top: 1px solid #e9edef;
      font-size: 12px;
      color: #667781;
      margin-top: 8px;
      overflow: hidden;
      padding-bottom: 0px !important;
      width: 100%;
      float: left;
      padding: 5px 3px;

      .status {
        float: right;
      }
    }

    .client-info {
      overflow: hidden;
      float: left;
      padding-top: 1px;

      .client-name {
        font-size: 17px;
        font-weight: 400;
        color: #111b21;
      }

      .client-vehicle {
        font-weight: 400;
        font-size: 13px;
        color: #667781;
      }
    }
  }
}
</style>
